/* eslint-disable prefer-destructuring */
import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Doughnut } from 'react-chartjs-2';
import {
  Box,
  Card,
  Button,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  colors,
  makeStyles,
  useTheme,
  CircularProgress
} from '@material-ui/core';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';

const useStyles = makeStyles(() => ({
  root: {
    height: '100%'
  }
}));

const colorMap = [
  colors.red[600],
  colors.blue[600],
  colors.green[600],
  colors.orange[600],
  colors.purple[600],
  colors.grey[600]
];

function countryToFlag(isoCode) {
  return typeof String.fromCodePoint !== 'undefined'
    ? isoCode
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(char.charCodeAt(0) + 127397)
        )
    : isoCode;
}

const UsersByCountry = ({ className, data, ...rest }) => {
  const classes = useStyles();
  const theme = useTheme();
  let otherCountries = data?.active;

  const options = {
    animation: false,
    cutoutPercentage: 80,
    layout: { padding: 0 },
    legend: {
      display: false
    },
    maintainAspectRatio: false,
    responsive: true,
    tooltips: {
      backgroundColor: theme.palette.background.default,
      bodyFontColor: theme.palette.text.secondary,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      enabled: true,
      footerFontColor: theme.palette.text.secondary,
      intersect: false,
      mode: 'index',
      titleFontColor: theme.palette.text.primary
    }
  };

  const onlyCountries =
    data &&
    Object.entries(data)?.filter(([key]) => {
      switch (key) {
        case 'active':
          return false;
        case 'count':
          return false;
        default:
          return true;
      }
    });

  onlyCountries?.sort((a, b) => b[1] - a[1]);
  const fiveBestCountry = onlyCountries?.slice(0, 5);

  const textData = fiveBestCountry?.map((item, index) => {
    return {
      title: item[0],
      value: item[1],
      icon: countryToFlag(item[0]),
      color: colorMap[index]
    };
  });

  fiveBestCountry?.forEach((item) => {
    otherCountries -= item[1];
  });

  textData?.push({
    title: 'Others',
    value: otherCountries,
    icon: '○',
    color: colors.grey[600]
  });

  const onlyValues = fiveBestCountry?.map((item) => item[1]);
  const onlyKeys = fiveBestCountry?.map((item) => item[0]);

  const chartData = data && {
    datasets: [
      {
        data: [...onlyValues, otherCountries],
        backgroundColor: colorMap,
        borderWidth: 8,
        borderColor: colors.common.white,
        hoverBorderColor: colors.common.white
      }
    ],
    labels: [...onlyKeys, 'Others']
  };

  return (
    <Card className={clsx(classes.root, className)} {...rest}>
      <CardHeader title="Users by Country" />
      <Divider />

      {data ? (
        <CardContent>
          <Box height={300} position="relative">
            <Doughnut data={chartData} options={options} />
          </Box>
          <Box display="flex" justifyContent="center" mt={2}>
            {textData.map(({ color, icon, title, value }) => (
              <Box key={title} p={1} textAlign="center">
                <Typography>{icon}</Typography>
                <Typography color="textPrimary" variant="body1">
                  {title}
                </Typography>
                <Typography style={{ color }} variant="h2">
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      ) : (
        <CircularProgress />
      )}
      <Divider />
      <Box display="flex" justifyContent="flex-end" p={2}>
        <Button
          color="primary"
          endIcon={<ArrowRightIcon />}
          size="small"
          variant="text"
        >
          Overview
        </Button>
      </Box>
    </Card>
  );
};

UsersByCountry.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object
};

export default UsersByCountry;
